import { createListenerMiddleware } from '@reduxjs/toolkit'
import { LOCATION_CHANGE } from 'lib/routing/appRouting'
import { SET_BYPASS_REGION_CANDIDATE_SETTING_ID, SET_BYPASS_REGION_EXCLUSION_OFFER_IDS, SET_BYPASS_REGION_SETTING_ID } from 'actions/actionConstants'
import { getOfferBypassRegionFilterSetting } from 'api/promo'
import { isOfferRegionBypassDeniedByExperiment } from 'lib/experiments/offerRegionBypassExperiment'
import { getCookies, setCookie } from 'lib/web/cookieUtils'

// The cookie marks that this user's UTM *matched* a bypass region filter rule (candidate for the
// offer_region_bypass experiment). Whether bypass is applied is always re-derived from the current
// Optimizely bucket, so the experiment bucket stays the source of truth.
const MATCHED_COOKIE_KEY = 'bypass_region_setting_id'
const EXCLUSION_IDS_COOKIE_KEY = 'bypass_region_exclusion_ids'
const COOKIE_TTL_SECONDS = 7 * 24 * 60 * 60 // 7 days

const bypassRegionMiddleware = createListenerMiddleware()

// Apply candidate (both arms) + active (denied only for explicit control — fail-open) from a
// matched setting id.
function applyMatchedSetting(
  listenerApi: { getState: () => unknown; dispatch: (action: unknown) => unknown },
  matchedSettingId: string,
) {
  listenerApi.dispatch({ type: SET_BYPASS_REGION_CANDIDATE_SETTING_ID, settingId: matchedSettingId })

  if (!isOfferRegionBypassDeniedByExperiment(listenerApi.getState() as App.State)) {
    listenerApi.dispatch({ type: SET_BYPASS_REGION_SETTING_ID, settingId: matchedSettingId })
  }
}

bypassRegionMiddleware.startListening({
  type: LOCATION_CHANGE,
  effect: async(_action, listenerApi) => {
    const state = listenerApi.getState() as App.State
    // Already resolved (via SSR or a previous location change) — nothing to do.
    if (state.marketing.bypassRegionCandidateSettingId) {
      listenerApi.unsubscribe()
      return
    }

    const cookies = getCookies()
    const savedSettingId = cookies[MATCHED_COOKIE_KEY]
    if (savedSettingId) {
      const savedIds = cookies[EXCLUSION_IDS_COOKIE_KEY]
      if (savedIds) {
        listenerApi.dispatch({ type: SET_BYPASS_REGION_EXCLUSION_OFFER_IDS, offerIds: savedIds.split(',') })
      }
      applyMatchedSetting(listenerApi, savedSettingId)
      listenerApi.unsubscribe()
      return
    }

    const { source, medium, campaign, term } = state.utm
    if (!source) {
      listenerApi.unsubscribe()
      return
    }

    listenerApi.unsubscribe()

    try {
      const result = await getOfferBypassRegionFilterSetting({
        source: source || undefined,
        medium: medium || undefined,
        campaign: campaign || undefined,
        term: term || undefined,
      })

      // Offer exclusions are global and experiment-independent — always applied when present.
      if (result.offer_region_filter_exclusion_offer_ids.length > 0) {
        setCookie(EXCLUSION_IDS_COOKIE_KEY, result.offer_region_filter_exclusion_offer_ids.join(','), COOKIE_TTL_SECONDS, '/', 'Lax', document.domain)
        listenerApi.dispatch({ type: SET_BYPASS_REGION_EXCLUSION_OFFER_IDS, offerIds: result.offer_region_filter_exclusion_offer_ids })
      }

      if (result.setting_id) {
        setCookie(MATCHED_COOKIE_KEY, result.setting_id, COOKIE_TTL_SECONDS, '/', 'Lax', document.domain)
        applyMatchedSetting(listenerApi, result.setting_id)
      }
    } catch {
      // non-critical — bypass stays unset
    }
  },
})

export default bypassRegionMiddleware
